/*
  Iris mix-and-match quantity bundle.

  Bundler's quantity break is single-variant: its widget carries one
  data-variant-id and the theme form posts one hidden name="id", so "Buy 2"
  can only ever mean 2x the shade already selected. This element replaces it
  with per-unit slots and rewrites the add-to-cart payload into Shopify's
  items[] form so each unit can be a different variant.

  Each slot shows every shade as a swatch rather than a dropdown, so the slot
  is an ARIA radiogroup with roving tabindex: one tab stop per slot, arrows
  move between shades inside it.

  Custom element, not a DOMContentLoaded script, because product-info.js
  replaces the whole section on a variant change -- connectedCallback re-binds
  on the new copy for free.
*/
if (!customElements.get('iris-bundle-picker')) {
  customElements.define(
    'iris-bundle-picker',
    class IrisBundlePicker extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('.product__info-container, product-info, body').querySelector('form[data-type="add-to-cart-form"]');
        // The sticky add-to-cart bar is its own form, "<main form id>--alt",
        // rendered outside the info container. It has to add the bundle too.
        // getAttribute, not .id: the form holds an <input name="id">, which
        // shadows form.id and returns that input.
        this.stickyForm = this.form && document.getElementById(`${this.form.getAttribute('id')}--alt`);
        this.forms = [this.form, this.stickyForm].filter(Boolean);
        this.tiers = Array.from(this.querySelectorAll('[data-iris-tier]'));
        if (!this.tiers.length) return;

        this.onTierChange = this.onTierChange.bind(this);
        this.onSlotChange = this.onSlotChange.bind(this);
        this.onSubmitCapture = this.onSubmitCapture.bind(this);

        this.addEventListener('change', (e) => {
          if (e.target.matches('[data-iris-tier-input]')) this.onTierChange();
          else if (e.target.matches('[data-iris-slot]')) this.onSlotChange(e.target);
        });

        // A radio cannot be unchecked by clicking it again -- the browser fires
        // no change event and leaves it checked. Sampling the state on
        // pointerdown, before the click is applied, is what makes the second
        // click on an already-selected tier mean "cancel the bundle".
        this.addEventListener('pointerdown', (e) => {
          const row = e.target.closest('.iris-bundle__row');
          if (!row) return;
          const input = row.querySelector('[data-iris-tier-input]');
          this.wasChecked = !!(input && input.checked);
        });

        this.addEventListener('click', (e) => {
          const swatch = e.target.closest('[data-iris-option]');
          if (swatch) { this.pick(swatch); return; }

          const row = e.target.closest('.iris-bundle__row');
          if (!row) return;
          const input = row.querySelector('[data-iris-tier-input]');
          if (input && this.wasChecked && input.checked) {
            input.checked = false;
            this.onTierChange();
          }
        });

        this.addEventListener('keydown', (e) => this.onKeydown(e));

        // Capture phase: ProductForm binds its own submit listener in its
        // constructor, and builds FormData synchronously inside it. Capturing
        // puts this ahead of that handler so the inputs it reads are already
        // rewritten, without forking product-form.js.
        this.forms.forEach((f) => f.addEventListener('submit', this.onSubmitCapture, true));

        // A variant change rewrites the price and quantity markup around us, so
        // the tier's quantity lock has to be re-asserted afterwards. PUB_SUB_EVENTS
        // is a `const` in pubsub.js, so it is a bare global, not window.PUB_SUB_EVENTS.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => this.onTierChange());
        }

        this.querySelectorAll('[data-iris-swatches]').forEach((g) => this.setRovingTabstop(g, null));
        this.onTierChange();
      }

      disconnectedCallback() {
        (this.forms || []).forEach((f) => f.removeEventListener('submit', this.onSubmitCapture, true));
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        // A re-rendered picker starts empty, so the card must drop the bundle.
        window.IrisBundleState = { complete: false };
        document.dispatchEvent(new CustomEvent('iris:bundle-change', { detail: { complete: false } }));
      }

      /* ---- floating add-to-cart card --------------------------------- */

      /* snippets/iris-sticky-add.liquid shows the bundle instead of the
         single product once a tier is chosen and every slot has a shade.
         It only needs to be told; its button submits the main form, which
         lands in onSubmitCapture like any other submit. */
      isComplete(tier) {
        if (!tier) return false;
        return Array.from(tier.querySelectorAll('[data-iris-slot]')).every((sel) => !!sel.value);
      }

      announce() {
        const tier = this.activeTier;
        const detail = { complete: this.isComplete(tier) };
        if (detail.complete) {
          const label = tier.querySelector('.iris-bundle__label');
          const price = tier.querySelector('[data-iris-price]');
          const was = tier.querySelector('[data-iris-was]');
          detail.qty = parseInt(tier.dataset.qty, 10) || 1;
          // What the card needs to work out the total saving against the
          // compare-at price: the chosen variants and the tier's percentage.
          detail.ids = Array.from(tier.querySelectorAll('[data-iris-slot]')).map((sel) => sel.value);
          detail.pct = parseInt(tier.dataset.pct, 10) || 0;
          detail.label = label ? label.textContent.trim() : '';
          detail.price = price ? price.textContent.trim() : '';
          detail.was = was ? was.textContent.trim() : '';
        }
        // Kept for a card that connects later: a price-changing shade makes
        // product-info.js re-render the section, card included.
        window.IrisBundleState = detail;
        document.dispatchEvent(new CustomEvent('iris:bundle-change', { detail }));
      }

      /* ---- swatches --------------------------------------------------- */

      swatchesIn(group) {
        return Array.from(group.querySelectorAll('[data-iris-option]:not([disabled])'));
      }

      /* A radiogroup is one tab stop, not N. The chosen swatch holds it; with
         nothing chosen yet it falls to the first shade so the group is still
         reachable by keyboard. */
      setRovingTabstop(group, chosen) {
        const all = this.swatchesIn(group);
        const stop = chosen || all[0];
        group.querySelectorAll('[data-iris-option]').forEach((b) => {
          b.tabIndex = b === stop ? 0 : -1;
        });
      }

      pick(swatch) {
        if (swatch.disabled) return;
        const slot = swatch.closest('[data-iris-picker]');
        const group = swatch.closest('[data-iris-swatches]');
        const select = slot.querySelector('[data-iris-slot]');

        group.querySelectorAll('[data-iris-option]').forEach((b) =>
          b.setAttribute('aria-checked', String(b === swatch)));
        this.setRovingTabstop(group, swatch);

        // The slot label doubles as the "COLOR: <name>" readout the variant
        // picker shows, so the discs themselves need no captions.
        const chosen = slot.querySelector('[data-iris-chosen]');
        if (chosen) chosen.textContent = swatch.dataset.name || '';

        select.value = swatch.dataset.value;
        // The change listener above drives repricing and validation, so the
        // select stays the single source of truth for everything downstream.
        select.dispatchEvent(new Event('change', { bubbles: true }));

        this.selectThemeShade(swatch.dataset.gang, swatch.dataset.value);
      }

      /* Tapping a bundle swatch moves the PDP's own shade selection too, so
         the "COLOR:" label, the highlighted swatch and the gallery agree with
         the slot that was just filled -- but it deliberately does NOT dispatch
         `change` on the theme's radio. That event is what makes product-info.js
         re-render the section from the server, and the flash of replaced markup
         reads as a page reload while the shopper is still filling slots.

         Everything a shade swap actually changes is done in place instead:
           - the gallery, by iris-gang-media.js (every shade's media is already
             in the DOM, so this needs no server data)
           - the "COLOR: x" legend, by variant-selects' own updateSelectionMetadata
           - the highlight ring, which is plain CSS on :checked
           - the hidden name="id" inputs and the ?variant= URL, below

         The one thing that cannot be faked client-side is a price change, so a
         shade that costs something different falls back to dispatching change
         and letting the theme re-render. Every shade on this product is the
         same price, so in practice that path does not run. */
      selectThemeShade(connect, variantId) {
        if (!connect) return;

        if (window.IrisGangMedia) window.IrisGangMedia.apply(connect);

        const selects = document.querySelector('variant-selects[data-primary]');
        const input = selects && selects.querySelector(`input[data-gang-connect="${connect}"]`);
        if (!input || input.checked || input.disabled) return;

        const info = this.closest('product-info') || document.querySelector('product-info');
        const current = info && info.querySelector('input[name="id"]');

        input.checked = true;

        if (!info || this.priceOf(variantId) !== this.priceOf(current && current.value)) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        if (typeof selects.updateSelectionMetadata === 'function') {
          selects.updateSelectionMetadata({ target: input });
        }
        // The theme's own method, so the sticky bar and installment forms get
        // the same id the main form does.
        if (typeof info.updateVariantInputs === 'function') info.updateVariantInputs(variantId);
        if (typeof info.updateURL === 'function') info.updateURL(info.dataset.url, variantId);
      }

      /* Shade prices come from the slot <select>, which already lists every
         variant with a data-price. An id that is not there compares unequal to
         everything (NaN), so anything unexpected takes the safe re-render path. */
      priceOf(variantId) {
        if (!variantId) return NaN;
        const opt = this.querySelector(`[data-iris-slot] option[value="${variantId}"]`);
        return opt ? opt.dataset.price : NaN;
      }

      get activeTier() {
        const input = this.querySelector('[data-iris-tier-input]:checked');
        return input ? input.closest('[data-iris-tier]') : null;
      }

      onTierChange() {
        const active = this.activeTier;
        this.setQuantityLocked(active ? (parseInt(active.dataset.qty, 10) || 1) : null);
        this.tiers.forEach((tier) => {
          const slots = tier.querySelector('[data-iris-slots]');
          if (!slots) return;
          const on = tier === active;
          // Single-variant products: the slots are pre-filled and never shown.
          slots.hidden = !on || slots.hasAttribute('data-iris-fixed');
          // Controls inside a hidden tier must not be validated, submitted or
          // reachable by Tab.
          slots.querySelectorAll('[data-iris-slot]').forEach((sel) => { sel.disabled = !on; });
          slots.querySelectorAll('[data-iris-option]').forEach((b) => {
            // Sold-out shades are marked aria-disabled in Liquid and stay
            // disabled whatever the tier does.
            if (b.getAttribute('aria-disabled') === 'true') return;
            b.disabled = !on;
          });
        });
        this.clearError();
        if (active) this.repriceTier(active);
        this.announce();
      }

      /* qty === null unlocks the theme's stepper and restores what the shopper
         had typed; a number locks it and shows the bundle's count instead.
         Disabled, not hidden: the number is the point. */
      setQuantityLocked(qty) {
        this.forms.forEach((form) => {
          const wrap = form.querySelector('.product-form__quantity');
          if (!wrap) return;
          const input = wrap.querySelector('input[name="quantity"]');

          if (qty === null) {
            wrap.classList.remove('iris-bundle-qty-locked');
            wrap.querySelectorAll('input[name="quantity"], .quantity__button').forEach((el) => {
              el.disabled = false;
            });
            if (input && input.dataset.irisPrevQty != null) {
              input.value = input.dataset.irisPrevQty;
              delete input.dataset.irisPrevQty;
            }
            return;
          }

          if (input && input.dataset.irisPrevQty == null) input.dataset.irisPrevQty = input.value;
          wrap.classList.add('iris-bundle-qty-locked');
          wrap.querySelectorAll('input[name="quantity"], .quantity__button').forEach((el) => {
            el.disabled = true;
          });
          if (input) input.value = qty;
        });
      }

      onSlotChange(select) {
        const slot = select.closest('[data-iris-picker]');
        if (slot) slot.removeAttribute('data-iris-invalid');
        const tier = select.closest('[data-iris-tier]');
        if (tier) this.repriceTier(tier);
        this.clearError();
        this.announce();
      }

      /* Price preview only -- the real discount is a Shopify automatic discount
         applied in the cart. Recomputed from the chosen shades so a future
         price difference between variants shows up honestly; slots not yet
         chosen fall back to the tier's base unit price. */
      repriceTier(tier) {
        const priceEl = tier.querySelector('[data-iris-price]');
        if (!priceEl) return;
        const pct = parseInt(tier.dataset.pct, 10) || 0;
        const qty = parseInt(tier.dataset.qty, 10) || 1;
        const selects = Array.from(tier.querySelectorAll('[data-iris-slot]'));

        let full = 0;
        if (selects.length) {
          const base = this.basePrice();
          selects.forEach((sel) => {
            const opt = sel.selectedOptions && sel.selectedOptions[0];
            const p = opt && opt.dataset.price ? parseInt(opt.dataset.price, 10) : base;
            full += isNaN(p) ? base : p;
          });
        } else {
          full = this.basePrice() * qty;
        }

        const discounted = full - Math.round((full * pct) / 100);
        priceEl.textContent = this.money(discounted);
        const wasEl = tier.querySelector('[data-iris-was]');
        if (wasEl) wasEl.textContent = this.money(full);
      }

      basePrice() {
        const opt = this.querySelector('[data-iris-slot] option[data-price]');
        return opt ? parseInt(opt.dataset.price, 10) || 0 : 0;
      }

      money(cents) {
        try {
          if (window.theme && theme.Currency && theme.shopSettings) {
            return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat);
          }
        } catch (e) { /* fall through */ }
        return (cents / 100).toFixed(2);
      }

      clearError() {
        this.querySelectorAll('[data-iris-error]').forEach((el) => { el.hidden = true; });
      }

      showError(tier) {
        const el = tier.querySelector('[data-iris-error]');
        if (el) el.hidden = false;
      }

      onSubmitCapture(evt) {
        // Main form or the sticky bar -- whichever was submitted gets the items.
        const form = evt.currentTarget;
        const tier = this.activeTier;
        const qty = tier ? parseInt(tier.dataset.qty, 10) || 1 : 1;
        const extras = this.extraItems(form);

        // No bundle: the theme's ordinary single-variant path. Leave it alone,
        // unless another block wants extra lines added with it (the engraving
        // block's "Custom Engraving" item) -- that needs items[] too.
        if (qty < 2) {
          if (!extras.length) return;
          const id = form.querySelector('input[name="id"]');
          const q = form.querySelector('input[name="quantity"]');
          if (!id || !id.value) return;
          const units = new Map([[id.value, Math.max(parseInt(q && q.value, 10) || 1, 1)]]);
          this.writeItems(form, units, extras);
          return;
        }

        const selects = Array.from(tier.querySelectorAll('[data-iris-slot]'));
        const chosen = [];
        let missing = false;
        selects.forEach((sel) => {
          if (!sel.value) {
            const slot = sel.closest('[data-iris-picker]');
            if (slot) slot.setAttribute('data-iris-invalid', '');
            missing = true;
            return;
          }
          chosen.push(sel.value);
        });

        if (missing || chosen.length !== qty) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this.showError(tier);
          // Submitted from the sticky bar or the floating card, the picker may
          // be off screen; bring it back so the empty slot is visible.
          const r = this.getBoundingClientRect();
          if (r.top < 0 || r.bottom > window.innerHeight) this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const bad = tier.querySelector('[data-iris-picker][data-iris-invalid] [data-iris-option]:not([disabled])');
          if (bad) bad.focus();
          return;
        }

        // Same shade picked twice is one line of quantity 2, not two lines --
        // /cart/add rejects a payload that names the same variant twice.
        const counts = new Map();
        chosen.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
        this.writeItems(form, counts, extras);
      }

      /* Other blocks register functions on window.IrisCartExtras that return
         extra cart lines ({ id, quantity, properties }) to add alongside the
         product. Only the engraving block does today. */
      extraItems(form) {
        const fns = Array.isArray(window.IrisCartExtras) ? window.IrisCartExtras : [];
        return fns.reduce((all, fn) => {
          try { return all.concat(fn(form) || []); } catch (e) { return all; }
        }, []);
      }

      /* Rewrites the form into Shopify's items[] payload: one item per
         variant in `units` (Map id -> quantity), then any extra lines. */
      writeItems(form, units, extras) {
        // Line item properties on the form (e.g. the engraving block's
        // properties[Engraving], tied in with form="") apply to every unit.
        // /cart/add ignores top-level properties once items[] is used, so each
        // item carries its own copy.
        const props = Array.from(this.formFields(form)).filter(
          (el) => !el.disabled && /^properties\[.+\]$/.test(el.name) && (el.type !== 'checkbox' || el.checked)
        );

        this.injected = [];
        let i = 0;
        units.forEach((count, id) => {
          this.injected.push(this.hidden(`items[${i}][id]`, id));
          this.injected.push(this.hidden(`items[${i}][quantity]`, String(count)));
          props.forEach((el) => {
            this.injected.push(this.hidden(`items[${i}]${el.name.replace(/^properties/, '[properties]')}`, el.value));
          });
          i += 1;
        });
        (extras || []).forEach((item) => {
          this.injected.push(this.hidden(`items[${i}][id]`, String(item.id)));
          this.injected.push(this.hidden(`items[${i}][quantity]`, String(item.quantity || 1)));
          Object.keys(item.properties || {}).forEach((k) => {
            this.injected.push(this.hidden(`items[${i}][properties][${k}]`, item.properties[k]));
          });
          i += 1;
        });
        this.injected.forEach((el) => form.appendChild(el));

        // items[] and a top-level id/quantity are mutually exclusive on
        // /cart/add. Disabling (not removing) keeps them out of FormData while
        // leaving the nodes product-form.js and product-info.js hold references
        // to, then they go straight back.
        this.suspended = Array.from(
          form.querySelectorAll('input[name="id"]:not([disabled]), input[name="quantity"]:not([disabled])')
        ).concat(props);
        this.suspended.forEach((el) => { el.disabled = true; });

        // ProductForm reads FormData synchronously in its own submit handler,
        // which runs later in this same dispatch, so restoring on a timeout is
        // safe and guarantees the form is intact whatever happens next.
        setTimeout(() => this.restoreForm(), 0);
      }

      /* form.elements includes controls outside the form that join it
         through a form="" attribute, which querySelectorAll would miss. */
      formFields(form) {
        return form.elements || form.querySelectorAll('input, select, textarea');
      }

      restoreForm() {
        if (this.injected) { this.injected.forEach((el) => el.remove()); this.injected = null; }
        if (this.suspended) { this.suspended.forEach((el) => { el.disabled = false; }); this.suspended = null; }
      }

      hidden(name, value) {
        const el = document.createElement('input');
        el.type = 'hidden';
        el.name = name;
        el.value = value;
        el.setAttribute('data-iris-bundle-item', '');
        return el;
      }
    }
  );
}
